import type { ReactNode } from 'react'
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded'
import { StatusChip, type ChipSize } from './Chip'
import { Pressable } from './Pressable'
import { useNls } from '../../nls/nlsProvider'

export interface MissedPeriodsChipProps {
  missedPeriods?: readonly string[]
  label?: ReactNode
  ariaLabel?: string
  size?: ChipSize
  className?: string
  onClick?: () => void
  testId?: string
}

export const MissedPeriodsChip = ({
  missedPeriods = [],
  label,
  ariaLabel,
  size = 'sm',
  className,
  onClick,
  testId,
}: MissedPeriodsChipProps) => {
  const { t } = useNls()

  if (!label && missedPeriods.length === 0) return null

  const periodsFormatted = missedPeriods.join(', ')
  const effectiveLabel = label ?? (periodsFormatted
    ? t('NLSChecklistMissedPeriods', { periods: periodsFormatted })
    : t('NLSChecklistMissedPeriods', { periods: '' }))

  const defaultAria = ariaLabel ?? (typeof effectiveLabel === 'string' ? effectiveLabel : 'Missed periods')

  const content = (
    <StatusChip
      tone="warning"
      size={size}
      icon={<EventBusyRoundedIcon fontSize="inherit" />}
      className={['dsMissedPeriodsChip', className ?? ''].filter(Boolean).join(' ')}
    >
      {effectiveLabel}
    </StatusChip>
  )

  if (onClick) {
    return (
      <Pressable
        onClick={onClick}
        aria-label={defaultAria}
        data-testid={testId}
        className="inline-flex"
      >
        {content}
      </Pressable>
    )
  }

  return (
    <span aria-label={defaultAria} data-testid={testId} className="inline-flex">
      {content}
    </span>
  )
}
